import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import {
  Activity,
  ActivityStatus,
  PRIMARY_STATUSES,
  SECONDARY_STATUSES,
  STATUS_META,
  actionCountLabel,
  isWaiting,
  relativeTime,
  triggeringQuote,
} from '../../models/activity.model';
import {
  ActivityInboxService,
  describeHttpError,
} from '../../services/activity-inbox.service';
import { ActivityDetailDialogComponent } from '../activity-detail-dialog/activity-detail-dialog.component';

/** Rows per page. The feed is already fully in memory, so paging is a slicing
 * concern rather than another round trip. */
const PAGE_SIZE = 10;

/** Which activities this browser has already opened, so freshly proposed work
 * can be marked as new. Versioned like the tab bar's pinned-tabs key so the
 * shape can change without tripping over stale entries. */
const SEEN_STORAGE_KEY = 'air.aiReview.seenActivityIds.v1';

/** Cap on remembered ids — without one this grows unbounded, and an id old
 * enough to fall off the list no longer needs a new/seen distinction. */
const SEEN_LIMIT = 500;

function loadSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((id): id is string => typeof id === 'string'))
      : new Set();
  } catch {
    // Private browsing or a corrupt entry: treat everything as already seen
    // rather than lighting up every row as new.
    return new Set();
  }
}

/**
 * The AI Activity Review queue: everything the conversation router agent has
 * proposed for one client, newest first, with a detail dialog per row for
 * confirming, retrying, or denying the proposed actions.
 *
 * Always scoped to a client and always cross-status — one request, because the
 * `by_client` GSI has no status in its key. The whole feed ends up local, so
 * filtering, counts, and paging are computed here rather than re-fetched.
 *
 * Mounted only by the FA client page, which wraps tab content in a
 * `bds-scroll-region`; the queue therefore flows to its content height and
 * lets the host scroll, rather than claiming a height and scrolling its own
 * list, which would nest two scroll containers and trap the inner one.
 */
@Component({
  selector: 'air-review-queue',
  imports: [
    CommonModule,
    Button,
    SkeletonModule,
    Tooltip,
    ActivityDetailDialogComponent,
  ],
  templateUrl: './review-queue.component.html',
  styleUrl: './review-queue.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewQueueComponent {
  private readonly _inbox = inject(ActivityInboxService);

  /** The client whose activities this shows. Required — the host renders the
   * tab only once a client is open, so there is no unscoped mode. */
  readonly clientGuid = input.required<string>();

  /** Display name for {@link clientGuid}, shown in place of the raw GUID. */
  readonly clientName = input<string | null>(null);

  /** Whether anything in the loaded feed still needs confirmation, emitted
   * after every successful load. A host showing its own indicator (the FA tab
   * bar's update dot) would otherwise go stale the moment a reviewer confirms
   * or denies from the dialog. Not emitted on failure — the last known answer
   * beats inventing one from an error. */
  readonly needsAttention = output<boolean>();

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  private readonly _activities = signal<Activity[]>([]);
  private readonly _countsByStatus = signal<Record<ActivityStatus, number>>(
    {} as Record<ActivityStatus, number>,
  );
  private readonly _truncated = signal(false);
  private readonly _seenIds = signal<Set<string>>(loadSeenIds());

  /** Incremented per load so a superseded response can be discarded. */
  private _loadToken = 0;

  /** Empty means no filter — show every status. */
  readonly activeStatuses = signal<ReadonlySet<ActivityStatus>>(new Set());
  readonly showAllFilters = signal(false);
  readonly page = signal(0);

  readonly selectedActivity = signal<Activity | null>(null);
  readonly detailVisible = signal(false);

  readonly primaryStatuses = PRIMARY_STATUSES;
  readonly secondaryStatuses = SECONDARY_STATUSES;
  readonly statusMeta = STATUS_META;

  readonly totalCount = computed(() => this._activities().length);

  readonly filtered = computed(() => {
    const active = this.activeStatuses();
    if (active.size === 0) return this._activities();
    return this._activities().filter((activity) => active.has(activity.status));
  });

  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / PAGE_SIZE)),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.pageCount() }, (_, i) => i),
  );

  readonly visible = computed(() => {
    const start = this.page() * PAGE_SIZE;
    return this.filtered().slice(start, start + PAGE_SIZE);
  });

  /** "Showing 1–10 of 24", or empty when there's nothing to show. */
  readonly rangeLabel = computed(() => {
    const total = this.filtered().length;
    if (total === 0) return '';
    const start = this.page() * PAGE_SIZE + 1;
    return `Showing ${start}\u2013${Math.min(start + PAGE_SIZE - 1, total)} of ${total}`;
  });

  /** "12 of 24 activities" while filtered, "24 activities" when not. */
  readonly countLabel = computed(() => {
    const shown = this.filtered().length;
    const total = this.totalCount();
    const noun = total === 1 ? 'activity' : 'activities';
    return shown === total
      ? `${total} ${noun}`
      : `${shown} of ${total} ${noun}`;
  });

  /** "5 awaiting review · 1 executing · 1 failed" — only the actionable
   * statuses, and only those actually present. */
  readonly summaryLabel = computed(() => {
    const counts = this._countsByStatus();
    return PRIMARY_STATUSES.filter((status) => (counts[status] ?? 0) > 0)
      .map((status) => `${counts[status]} ${STATUS_META[status].summaryLabel}`)
      .join(' \u00b7 ');
  });

  /** Set when the client filled a whole page, so the counts above are floors
   * rather than totals. */
  readonly countsCaveat = computed(() =>
    this._truncated()
      ? 'This client has more activities than one page holds; counts are a lower bound.'
      : null,
  );

  constructor() {
    // Loads on creation and again whenever the host switches clients. An
    // effect rather than ngOnInit because clientGuid is an input that changes
    // in place — the FA layout reuses one instance across client selections.
    effect(() => {
      const guid = this.clientGuid();
      this.page.set(0);
      void this._load(guid);
    });
  }

  async refresh(): Promise<void> {
    await this._load(this.clientGuid());
  }

  private async _load(clientGuid: string): Promise<void> {
    // Switching clients faster than the requests resolve would otherwise let
    // an earlier response land last and show the previous client's activities.
    const token = ++this._loadToken;
    this.loading.set(true);
    this.error.set(null);
    try {
      const feed = await this._inbox.loadClientFeed(clientGuid);
      if (token !== this._loadToken) return;
      this._activities.set(feed.activities);
      this._countsByStatus.set(feed.countsByStatus);
      this._truncated.set(feed.truncated);
      this._clampPage();
      this.needsAttention.emit(
        (feed.countsByStatus['needs_confirmation'] ?? 0) > 0,
      );
    } catch (err) {
      if (token !== this._loadToken) return;
      this.error.set(describeHttpError(err));
    } finally {
      if (token === this._loadToken) this.loading.set(false);
    }
  }

  countFor(status: ActivityStatus): number {
    return this._countsByStatus()[status] ?? 0;
  }

  isActive(status: ActivityStatus): boolean {
    return this.activeStatuses().has(status);
  }

  toggleStatus(status: ActivityStatus): void {
    const next = new Set(this.activeStatuses());
    if (!next.delete(status)) next.add(status);
    this.activeStatuses.set(next);
    this.page.set(0);
  }

  resetFilters(): void {
    this.activeStatuses.set(new Set());
    this.page.set(0);
  }

  get hasFilters(): boolean {
    return this.activeStatuses().size > 0;
  }

  goToPage(page: number): void {
    this.page.set(Math.min(Math.max(page, 0), this.pageCount() - 1));
  }

  isNew(activity: Activity): boolean {
    return !this._seenIds().has(activity.activity_id);
  }

  openActivity(activity: Activity): void {
    this._markSeen(activity.activity_id);
    this.selectedActivity.set(activity);
    this.detailVisible.set(true);
  }

  /** The dialog mutated the activity (note, retry, deny), so the row's status,
   * notes, and proposal may all be stale. */
  async onActivityChanged(): Promise<void> {
    await this.refresh();
    const selected = this.selectedActivity();
    if (!selected) return;
    const fresh = this._activities().find(
      (activity) => activity.activity_id === selected.activity_id,
    );
    if (fresh) this.selectedActivity.set(fresh);
  }

  // Template helpers — thin wrappers so the markup stays declarative.
  quoteFor = triggeringQuote;
  actionCountFor = actionCountLabel;
  waiting = isWaiting;
  since = relativeTime;

  hasNotes(activity: Activity): boolean {
    return activity.notes.length > 0;
  }

  private _clampPage(): void {
    if (this.page() > this.pageCount() - 1) this.page.set(0);
  }

  private _markSeen(activityId: string): void {
    if (this._seenIds().has(activityId)) return;
    const next = new Set(this._seenIds());
    next.add(activityId);
    // Newest ids are appended, so trimming from the front drops the oldest.
    const trimmed =
      next.size > SEEN_LIMIT
        ? new Set([...next].slice(next.size - SEEN_LIMIT))
        : next;
    this._seenIds.set(trimmed);
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...trimmed]));
    } catch {
      // Storage unavailable — the in-memory set still works for this session.
    }
  }
}
